import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountHeadModal from './AccountHeadModal';
import Pagination from '../../common/Pagination';

function AccountHeadIndex() {
  // Session validation
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedAccountHead, setSelectedAccountHead] = useState(null);

  // Dropdown data states
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return; // Prevent calls without required data

    setLoading(true);
    Promise.all([
      fetchtableData(),
      fetchbalanceSheetData()
    ]).finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  const fetchtableData = async () => {
    try {
      const response = await fetchData('AccountHeadApi', `/${companyId}`, token);
      console.log('AccountHead data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('AccountHead data loaded successfully:', response.response_data);
      } else {
        console.log('AccountHead API failed or returned no data');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching AccountHead Details:', error);
      setData([]);
    }
  };

  const fetchbalanceSheetData = async () => {
    try {
      // Fetch Balance Sheet Heads with better error handling
      const balanceSheetResponse = await fetchData('BalanceSheetHeadApi', `/${companyId}`, token);
      console.log('Balance Sheet Response:', balanceSheetResponse);

      if (balanceSheetResponse && balanceSheetResponse.responseCode === '1111' && balanceSheetResponse.response_data) {
        setBalanceSheetHeads(balanceSheetResponse.response_data);
        console.log('Balance Sheet Heads loaded successfully:', balanceSheetResponse.response_data);
      } else {
        console.log('Balance Sheet API failed or returned no data');
        setBalanceSheetHeads([]);
      }

    } catch (error) {
      console.error('Error fetching balance sheet data:', error);
      setBalanceSheetHeads([]);
    }
  };

  const handleDelete = async () => {
    deleteData('AccountHeadApi', `${deleteItemId}`, token)
      .then(() => {
        fetchtableData(); // Refresh data after deletion
        setDeleteItemId(null); // Reset the item ID
        setConfirmDialogOpen(false); // Close the dialog
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const handleActivate = (itemId) => {
    activateData('AccountHeadApi', `${itemId}`, token)
      .then(() => {
        fetchtableData(); // Refresh data after activation
      })
      .catch((error) => {
        console.error('Error activating record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Modal handlers
  const handleOpenModal = (mode, accountHead = null) => {
    setModalMode(mode);
    setSelectedAccountHead(accountHead);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAccountHead(null);
  };

  const handleSaveAccountHead = () => {
    fetchtableData(); // Refresh data
  };

  // Helper functions - defined before use
  const getTypeDisplay = (isType) => {
    return isType ? 'Income' : 'Expense';
  };

  const getBalanceSheetHeadName = (balanceSheetHeadId) => {
    const balanceSheetHead = balanceSheetHeads.find(head => head.Id === balanceSheetHeadId);
    return balanceSheetHead ? balanceSheetHead.Name : 'N/A';
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const balanceSheetHeadName = getBalanceSheetHeadName(item.BalanceSheetHeadId).toLowerCase();
    const typeDisplay = getTypeDisplay(item.IsType).toLowerCase();
    return (
      item.Name?.toLowerCase().includes(searchLower) ||
      balanceSheetHeadName.includes(searchLower) ||
      typeDisplay.includes(searchLower)
    );
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Define table columns
  const columns = [
    { field: 'Name', header: 'Account Head Name' },
    { field: 'BalanceSheetHead', header: 'Balance Sheet Head' },
    { field: 'IsType', header: 'Type' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: "0 1px 4px rgba(13, 118, 110, 0.3)",
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      <Tooltip title="Edit Account Head" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('edit', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          ✏️
        </Button>
      </Tooltip>

      <Tooltip title={item.IsActive ? "Delete Account Head" : "Activate Account Head"} arrow>
        <Button
          size="small"
          onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: item.IsActive
              ? '0 1px 4px rgba(255, 107, 107, 0.3)'
              : '0 1px 4px rgba(78, 205, 196, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          {item.IsActive ? '🗑️' : '✅'}
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        <div className="suite-page-panel" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: "Segoe UI, sans-serif",
        }}>
          {/* Header Section */}
          <div className="suite-section-heading" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: "Segoe UI, sans-serif",
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: "Segoe UI, sans-serif", }}>Account Head</span>
                </h4>

              </div>
            </div>
            <Tooltip title="Create a new account head" arrow>
              <Button
                variant="contained"
                onClick={() => handleOpenModal('create')}
                className="action-button"
                style={{
                  background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)",
                  border: 'none',
                  minHeight: '26px',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span style={{ fontSize: '12px' }}>+</span>
                Create Account Head
              </Button>
            </Tooltip>
          </div>

          {/* Search Bar */}
          <div style={{ marginBottom: '12px' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, balance sheet head, or type..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#0d766e", fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '13px',
                  '&:hover fieldset': {
                    borderColor: "#0d766e",
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: "#0d766e",
                  },
                },
              }}
            />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="account head table">
                <TableHead>
                  <TableRow style={{
                    background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)"
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '8px 12px',
                        textAlign: 'left'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '8px 12px',
                      textAlign: 'right'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.Name}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {getBalanceSheetHeadName(item.BalanceSheetHeadId)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: item.IsType ? '#e8f5e8' : '#fff3e0',
                          color: item.IsType ? '#2e7d32' : '#f57c00'
                        }}>
                          {getTypeDisplay(item.IsType)}
                        </span>
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this account head record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* AccountHead Modal */}
        <AccountHeadModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveAccountHead}
          accountHeadData={selectedAccountHead}
          mode={modalMode}
          balanceSheetHeads={balanceSheetHeads}
          existingAccountHeads={data}
        />     
        
      </div>
    </Fade >
  );
}

export default AccountHeadIndex;